import React, { useState, useEffect } from 'react'
import { Eye } from 'lucide-react'
import { clsx } from 'clsx'
import { A11ySheet } from './A11ySheet'
import { useCSSBridge } from '../bridge'
import { useA11yStore } from '../store'

interface A11yWidgetProps {
  bottomOffset?: number
  alwaysVisible?: boolean
}

export function A11yWidget({
  bottomOffset = 80,
  alwaysVisible = false,
}: A11yWidgetProps): React.ReactElement | null {
  const [sheetOpen, setSheetOpen] = useState(false)
  const widgetEnabled = useA11yStore((state) => state.widgetEnabled)
  const rehydrate = useA11yStore((state) => state.rehydrate)

  useCSSBridge()

  useEffect(() => {
    rehydrate()
  }, [rehydrate])

  const shouldRender = widgetEnabled || alwaysVisible

  if (!shouldRender) {
    return null
  }

  return (
    <>
      {/* z-40 sits below every dialog/sheet/drawer (z-50), so an open sheet covers this button
          instead of the button floating over it. pointer-events-auto stays so the widget is not
          locked out by Radix's body pointer-events:none while a modal is open. */}
      <div
        className="group fixed z-40 pointer-events-auto"
        style={{ bottom: `${bottomOffset}px`, right: '1.5rem' }}
      >
        <button
          onClick={() => setSheetOpen(true)}
          className={clsx(
            // w-10 h-10 — same exact dimensions as SearchTrigger's circle
            // button (apps/storefront/src/components/chrome/SearchTrigger.tsx)
            // so the two floating circular buttons read as the same size.
            'w-10 h-10 rounded-full',
            'bg-slate-600 hover:bg-slate-700 dark:bg-slate-500 dark:hover:bg-slate-400',
            'text-white',
            'flex items-center justify-center',
            'transition-colors duration-200',
            'shadow-lg hover:shadow-xl',
            'focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-slate-500'
          )}
          aria-label="Open accessibility settings"
          type="button"
        >
          <Eye className="h-5 w-5" />
        </button>

        {/* Tooltip — matches the canonical pattern used by SearchTrigger,
            LogoMenu's WorkspaceTip, etc.: positioned to the left via
            end-full + top-1/2 -translate-y-1/2, dark bg/light text in light
            mode, light bg/dark text in dark mode. CSS-only, no new
            dependency — @radix-ui/react-tooltip isn't installed here. */}
        <span
          aria-hidden
          className="
            absolute end-full me-3 top-1/2 -translate-y-1/2 z-30
            px-3 py-1.5 rounded-lg
            bg-slate-800 dark:bg-sand-100
            text-white dark:text-slate-800
            text-sm font-medium font-sans whitespace-nowrap
            opacity-0 group-hover:opacity-100
            pointer-events-none transition-opacity duration-150 shadow-sm
          "
        >
          Accessibility settings
        </span>
      </div>

      <A11ySheet open={sheetOpen} onOpenChange={setSheetOpen} />
    </>
  )
}
